
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAppContext } from 'contexts/AppContext';
import { useRouter } from 'next/router';
import { motion, AnimatePresence } from 'framer-motion';
import { checkMark, validate, track, trackChoosePlan, capitalize, shirtAsterisk, getCardType, tick, tryLocalStorage, trackTrialCreated, trackSubscriptionCreated, getPlanBillingAmount, scrollTo, getMotionProps } from 'utils';
import { getSubscriptionObj, initialSignUpInfo, REFERRAL, planA, planB, plans, signups, FLASH_SALE } from 'utils/signup';
import { E_ACCOUNT_CREATED } from 'utils/constants';
import { signUp, createSubscription, validateAccessCode, validateReferee, updatePlanSelected, signUpExistingUser, subscriptionSuccess  } from 'api';
import * as Sentry from "@sentry/nextjs";
import Head from 'next/head';
import Link from 'next/link';
import PillsNavigation from '@/components/modules/PillsNavigation/PillsNavigation';
import Input from '@/components/elements/Input/Input';
import Button from '@/components/elements/Button/Button';
import HintMessage from '@/components/elements/HintMessage/HintMessage';
import Plan from '@/components/elements/Plan/Plan';
import ShirtPrompt from '@/components/modules/ShirtPrompt/ShirtPrompt';
import Badge from '@/components/elements/Badge/Badge';
import EmojiRain from '@/components/modules/EmojiRain/EmojiRain';
import CardInputsV2 from '@/components/modules/CardInputsV2/CardInputsV2';
import FullScreenLoader from '@/components/modules/FullScreenLoader/FullScreenLoader';
import styles from './SignUp.module.scss';

const SignUp = ({stepRequested, content, type, res}) => {
    const DOES_AB = type !== "flash_sale";
    const router = useRouter();
    const { signUpInfo, setSignUpInfo, userStatus, setAppLoading, handleCreateUser, canProceedSignUp, proceed, apiHint, setApiHint, priceAssigned, setPriceAssigned } = useAppContext();
    const [validatingReferral, setValidatingReferral] = useState(type === REFERRAL)

    if(userStatus?.isActive) router.replace('/watch')

    useEffect(()=>{
        async function validateInitialParams(){
            if(type === REFERRAL){
                const {email} = router.query
                const res = await validateReferee(email)
                if(!res.data.valid) router.push('/signup')
                setValidatingReferral(false)
            }
        }
        validateInitialParams()
        return () =>{
            signUpInfo.completedSignUp && tryLocalStorage.set('signUpInfo', '')
        }
    }, [router, signUpInfo.completedSignUp, type])

    useEffect(()=>{
        if(signUpInfo.initialized){
            setSignUpInfo({...signUpInfo, shouldAb: DOES_AB})
            const {email, code} = router.query
            if(email || code) setSignUpInfo({...initialSignUpInfo, email, accessCode: {...signUpInfo.accessCode, code}})
            if(signUpInfo.userCreated) router.push({ query: { ...router.query, step: 2 } }, undefined)
        }
    }, [signUpInfo.initialized]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(()=>{
        let timeoutId;
        const validateStep = (stepRequested)=>{
            if(stepRequested === 1) return true;
            if(stepRequested === 2) return signUpInfo.userCreated || userStatus.loggedIn
            if(stepRequested === 3) return signUpInfo.userCreated && validate.plan(signUpInfo.plan, signUpInfo.customPrice).valid
            return false
        }
        async function tryGetAndValidateStep(){
            function getStep(){
                if(stepRequested<router.query.step) return stepRequested
                return validateStep(stepRequested) ? stepRequested : 1
            }
            const stepToTakeTo = getStep()
            router.replace({ query: { ...router.query, step: stepToTakeTo } }, undefined)
            timeoutId = setTimeout(() => {
                window.scrollTo({top: 0, behavior: 'smooth'});
            }, 50);
        }
        tryGetAndValidateStep()
        return () => clearTimeout(timeoutId);
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    function handleClickBack(){
        router.query.step > 1 && router.push({ query: { ...router.query, step: parseInt(router.query.step) - 1 } }, undefined)
    }

    return (
        <>
        <Head>
            <title>Signup | afterglow</title>
        </Head>
        <div className={styles.signUp}>
            {validatingReferral && <FullScreenLoader />}
            {signUpInfo.completedSignUp && <EmojiRain />}
            <PillsNavigation currentStep={router.query.step} onBack={handleClickBack} hideBackBtn={router.query.step<=1  || router.query.step === undefined} onClose={()=>{router.push('/'), tryLocalStorage.set('signUpInfo', '')}} fixed={true} />
            <SignUpBg />
            <main>
                <AnimatePresence>
                {router.query.step == 1 &&
                <motion.div initial={{opacity: 0, x: 500}} animate={{opacity: 1, x: 0,  transition:{duration: .5}}} exit={{opacity: 0, position:"absolute", x: -500, transition:{duration: .25}}} key={1}>
                    <StepOne content={content.stepOne} type={type} doesAb={DOES_AB} />
                </motion.div>
                }
                {router.query.step == 2 &&
                <motion.div initial={{opacity: 0, x: 500}} animate={{opacity: 1, x: 0, transition:{duration: .5}}} exit={{opacity: 0, position:"absolute", x: -500, transition:{duration: .25}}} key={2}>
                    <StepTwo content={content.stepTwo} type={type} doesAb={DOES_AB} />
                </motion.div>
                }
                {router.query.step == 3 &&
                <motion.div initial={{opacity: 0, x: 500}} animate={{opacity: 1, x: 0, transition:{duration: .5}}} exit={{opacity: 0, position:"absolute", x: -500, transition:{duration: .25}}} key={3}>
                    <StepThree content={content.stepThree} type={type} />
                </motion.div>
                }
                </AnimatePresence>
            </main>
        </div>
        </>
     );
}

export const SignUpBg = () => {
    return (
        <div className={styles.SignUpBg}>
            <div></div>
        </div>
    )
}

const StepTitle = ({title, subTitle}) => {
    return (
        <div className={styles.stepTitle}>
            <h2>{title}</h2>
            {subTitle &&
            <div>{subTitle}</div>
            }
        </div>
    )
}

export const StepOne = ({content, type, doesAb=false }) => {
    const router = useRouter();
    const { userStatus, setAppLoading, signUpInfo, setSignUpInfo, appLoading, handleCreateUser, canProceedSignUp, proceed, apiHint, setApiHint, priceAssigned, setPriceAssigned } = useAppContext()
    const [fieldsHint, setFieldsHint] = useState({type:"", message:""})
    const checkMarks = ['Feel good about your porn', 'Support women creators', 'Learn from pleasure experts', 'Prioritize your pleasure']


    useEffect(()=>{
        const emailValidation = validate.email(signUpInfo.email)
        const passwordValidation = validate.password(signUpInfo.password)
        if(!emailValidation.valid){
            setFieldsHint({type: "error", message: emailValidation?.message})
        }else if(!passwordValidation.valid){
            setFieldsHint({type: "error", message: passwordValidation?.message})
        }else{
            setFieldsHint({type: "", message: ""})
        }
    }, [signUpInfo.email, signUpInfo.password])

    const AccountCreated = () => {
        return (<section className={styles.accountCreated}>
            <StepTitle title="Account Created" subTitle="" />
            <div className={styles.loggedInUser}>
                Generating subscription for logged in user:
                <div>{userStatus.email}</div>
                <div className={styles.cta}>
                    <Button
                        onClick=
                            {() => {
                                proceed();
                            }}
                        >
                        Continue
                    </Button>
                </div>
            </div>
        </section>)
    }

    if(userStatus.loggedIn) return <AccountCreated />

    return (
        <section className={styles.stepOne}>
            <StepTitle title={content.title} subTitle={content.subTitle} />
            <div className={styles.stepOne__narrowContent}>
                <ul className={styles.stepOne__checklist}>
                    {checkMarks.map((item, i)=>
                    <li key={item}>
                        <div>{checkMark()}</div> {item}
                    </li>
                    )}
                </ul>
                {content.showCodeInput &&
                <div className= {styles.stepOne__promoCodeForm}>
                    <PromoCodeForm />
                </div>
                }
                <form onSubmit={(e)=>{
                    e.preventDefault();
                    handleCreateUser()
                }}>
                    <div className={styles.stepOne__createAccount}>
                        <div>Create Account</div>
                        <Input type="email"  autoFocus={true} value={signUpInfo.email} placeholder="Type your email" onChange={(email)=>setSignUpInfo({...signUpInfo, email})} valid={validate.email(signUpInfo.email).valid} theme="v2" disabled={signUpInfo.userCreated || type === REFERRAL} />
                        <Input type="password"  value={signUpInfo.password} placeholder="Create a password" onChange={(password)=>setSignUpInfo({...signUpInfo, password})} valid={validate.password(signUpInfo.password).valid} theme="v2" disabled={signUpInfo.userCreated} />
                        <HintMessage type={apiHint?.type} show={apiHint?.message!==""}>{apiHint?.message}</HintMessage>
                        <HintMessage type={fieldsHint?.type} show={fieldsHint?.message!==""}>{fieldsHint?.message}</HintMessage>
                        {type === REFERRAL && <HintMessage type="error" show={true}>Email cannot be changed until account is created.</HintMessage>}
                        <div className={styles.stopOne__detail}>By proceeding, you agree to the Terms of Service and any opt-ins.</div>
                    </div>
                    <div className={styles.stepOne__button}>
                        <Button active={canProceedSignUp() && !appLoading} type="submit">Continue</Button>
                    </div>
                </form>
            </div>
        </section>
    )
}

export const StepTwo = ({content, type, doesAb=false }) => {
    const router = useRouter()
    const { signUpInfo, setSignUpInfo, handleCreateUser, canProceedSignUp, proceed, apiHint, setApiHint, priceAssigned, setPriceAssigned, handleClick, updatePriceSelected } = useAppContext()
    const checkMarks = ['Porn you can feel good about', 'New content every week', 'Pleasure education', 'Guided pleasure journeys']
    const [showShirt, setShowShirt] = useState(false)
    const [hint, setHint] = useState({message:"", type:""})
    const ctaRef = useRef()

    useEffect(()=>{
        if(type === REFERRAL) setSignUpInfo({...signUpInfo, plan:content.plans[0], accessCode: {...signUpInfo.accessCode, valid: true}})
    }, [content.plans, setSignUpInfo, signUpInfo, type])

    useEffect(()=>{
        const validation = validate.plan(signUpInfo.plan, signUpInfo.customPrice)
        !validation.valid ? setHint({message: validation?.message, type:"error"}) : setHint({message:"", type:""})
    }, [signUpInfo.plan, signUpInfo.customPrice])

    const canProceed = useCallback(()=>{
        return validate.plan(signUpInfo.plan, signUpInfo.customPrice).valid
    }, [signUpInfo.plan, signUpInfo.customPrice])

    useEffect(()=>{
        setSignUpInfo({...signUpInfo, plan: null})
    },[])

    useEffect(()=>{
        if (type === 'flash_sale' && signUpInfo.plan){
            let interval = null
            if (signUpInfo.plan.price === 69){
                interval = 'year'
            } else if (signUpInfo.plan.price === 2.99){
                interval = 'day'
            } else if (signUpInfo.plan.price === 9.99){
                interval = 'month'
            }
            if (signUpInfo.plan.interval !== interval) {
                setSignUpInfo({
                    ...signUpInfo,
                    plan: {
                        ...signUpInfo.plan,
                        interval,
                    },
                })
            }
        }
    },[signUpInfo, type])

    return (
        <section className={styles.stepTwo}>
            <ShirtPrompt close={()=>setShowShirt(false)} show={showShirt} />
            <StepTitle title={content.title} />
            <div className={styles.stepTwo__checklist}>
                <div>Your membership includes</div>
                <ul>
                    {checkMarks.map((item, i)=>
                    <li key={item}>
                        <div>{checkMark()}</div> {item}
                    </li>
                    )}
                </ul>
            </div>

            <div className={styles.stepTwo__plans}>
                {type === "flash_sale" &&
                    content.plans[2].map((plan, i)=>
                        <Plan
                            key={plan.name}
                            nth={i + 1}
                            theme="v2"
                            plan={plan}
                            onClick={plan.isCustom ? null : (plan)=>{
                                setSignUpInfo({...signUpInfo, plan, customPrice:""});
                                if(!content.hideCtaButton) {
                                    scrollTo(ctaRef, 50, "bottom")
                                }
                            }}
                            selected={plan.isCustom ? signUpInfo.customPrice : signUpInfo.plan?.name === plan.name}
                            asterisk={plan.name==="VIP" && shirtAsterisk(setShowShirt)}
                            testId={`signUp__plan${i+1}`}
                            accessCode={signUpInfo.accessCode?.valid && signUpInfo.accessCode}
                            onChange={plan.isCustom ? (customPrice)=>{
                                setSignUpInfo({
                                    ...signUpInfo,
                                    customPrice,
                                    plan:null
                                })
                            } : null}
                            customPrice={plan.isCustom ? signUpInfo.customPrice : null}
                        />
                    )
                }
                {!signUpInfo.shouldAb && type === "main" &&
                    content.plans[0].map((plan, i)=>
                        <Plan key={plan.name} nth={i + 1}
                        theme="v2" plan={plan}
                        onClick={plan.isCustom ? null : (plan)=>{setSignUpInfo({...signUpInfo, plan, customPrice:""});
                        if(!content.hideCtaButton) scrollTo(ctaRef, 50, "bottom")}}
                        selected={plan.isCustom ? signUpInfo.customPrice : signUpInfo.plan?.name === plan.name}
                        asterisk={plan.name==="VIP" && shirtAsterisk(setShowShirt)}
                        testId={`signUp__plan${i+1}`}
                        accessCode={signUpInfo.accessCode?.valid && signUpInfo.accessCode}
                        onChange={plan.isCustom ? (customPrice)=>{setSignUpInfo({...signUpInfo, customPrice, plan:null})} : null}
                        customPrice={plan.isCustom ? signUpInfo.customPrice : null} />
                    )
                }


                {signUpInfo.abPriceDaily === "2.99" || priceAssigned === 1 ?
                    content.plans[0].map((plan, i)=>
                        <Plan key={plan.name} nth={i + 1}
                        theme="v2" plan={plan}
                        onClick={plan.isCustom ? null : (plan)=>{setSignUpInfo({...signUpInfo, plan, customPrice:""});
                        if(!content.hideCtaButton) scrollTo(ctaRef, 50, "bottom")}}
                        selected={plan.isCustom ? signUpInfo.customPrice : signUpInfo.plan?.name === plan.name}
                        asterisk={plan.name==="VIP" && shirtAsterisk(setShowShirt)}
                        testId={`signUp__plan${i+1}`}
                        accessCode={signUpInfo.accessCode?.valid && signUpInfo.accessCode}
                        onChange={plan.isCustom ? (customPrice)=>{setSignUpInfo({...signUpInfo, customPrice, plan:null})} : null}
                        customPrice={plan.isCustom ? signUpInfo.customPrice : null} />
                    )
                    :
                    <div></div>
                }

                {signUpInfo.abPriceDaily === "4.99" || priceAssigned === 2 ?
                    content.plans[1].map((plan, i)=>
                        <Plan key={plan.name} nth={i + 1}
                        theme="v2" plan={plan}
                        onClick={plan.isCustom ? null : (plan)=>{setSignUpInfo({...signUpInfo, plan, customPrice:""});
                        if(!content.hideCtaButton) scrollTo(ctaRef, 50, "bottom")}}
                        selected={plan.isCustom ? signUpInfo.customPrice : signUpInfo.plan?.name === plan.name}
                        asterisk={plan.name==="VIP" && shirtAsterisk(setShowShirt)}
                        testId={`signUp__plan${i+1}`}
                        accessCode={signUpInfo.accessCode?.valid && signUpInfo.accessCode}
                        onChange={plan.isCustom ? (customPrice)=>{setSignUpInfo({...signUpInfo, customPrice, plan:null})} : null}
                        customPrice={plan.isCustom ? signUpInfo.customPrice : null} />
                    )
                    :
                    <div></div>
                }
            </div>
            <div className={styles.stepTwo__hint}>
                <HintMessage type={hint?.type} show={hint?.message!==""}>{hint?.message}</HintMessage>
            </div>

            {!content.hideCtaButton &&
                <div className={styles.stepTwo__button} ref={ctaRef}>
                    <Button active={canProceed()} onClick={handleClick} type="submit">Continue</Button>
                </div>
            }
            <div className={styles['step_two_bottom_merchant_text']}>
                Charges will appear on your statement with the merchant name &quot;afterglow.&quot;
            </div>
        </section>
    )
}

const StepThree = ({content, type}) => {

    const router = useRouter();
    const { signUpInfo, setSignUpInfo, setAppLoading, appLoading } = useAppContext()
    const [subscriptionHints, setSubscriptionHints] = useState({message:"", type:""})

    const validateForm = useCallback(()=>{
        const toValidate = [validate.cardNumber(signUpInfo.cardNumber, getCardType(signUpInfo.cardNumber?.toString())), validate.expiryDate(signUpInfo.month, signUpInfo.year), validate.cvv(signUpInfo.cvv), validate.notEmpty("Zip code", signUpInfo.zipCode), validate.notEmpty("First name", signUpInfo.firstName), validate.notEmpty("Last name", signUpInfo.lastName)]
        return toValidate.reduce((acc, next) => {
            if (!acc.valid) return acc;
            return next;
        })
    }, [signUpInfo])

    useEffect(()=>{
        validateForm().valid ? setSubscriptionHints({type:"", message:""}) : setSubscriptionHints({type:"error", message: validateForm()?.message})
    }, [signUpInfo, validateForm])

    function canProceed(){
        return validateForm().valid;
    }

    async function handleCreateSubscription(){
        setAppLoading(true)
        const subscriptionObj = getSubscriptionObj({...signUpInfo, subscriptionAmount: getPlanBillingAmount(signUpInfo)})
        try{
            const response = await createSubscription(subscriptionObj)
            if(response.data.status === "success"){
                setSignUpInfo({...signUpInfo, completedSignUp: true})
                signUpInfo.accessCode.valid ? trackTrialCreated({...signUpInfo, type}, router.asPath) : trackSubscriptionCreated({...signUpInfo, type}, router.asPath)
                router.push({ query: { ...router.query, welcome: true } }, undefined, {shallow: true})
                router.push('subscription-success')
            }else{
                setSubscriptionHints({message: response.data?.errors[0], type:"error"})
            }
        }catch(e){
            Sentry.captureException(e);
            setSubscriptionHints({message: "There was an error, please check your credit card information and try again. Pending charges will disappear in 1-2 business days.", type:"error"})
        }
        setAppLoading(false)
    }

    return (
        <section className={styles.stepThree}>
            <StepTitle title={content.title} />
            <div className={styles.stepThree__narrowContent}>
                <div className={styles.stepThree__quote}>
                    <div>“As someone who watches porn for a living, it&#39;s been a long time since I&#39;ve been THIS turned on.”</div>
                    <div>Women of Sex Tech Board Member</div>
                </div>
                <div className={styles.stepThree__planSummary}>
                    <div className={styles.stepThree__sectionLabel}>Plan Summary</div>
                    <div>
                        <div>{signUpInfo.customPrice === "" ? signUpInfo?.plan?.name : "Pay What You Want"}</div>
                        <div>
                            <div>{capitalize(signUpInfo?.plan?.fee)} fee</div>
                            <div>${getPlanBillingAmount(signUpInfo)}</div>
                        </div>
                        {signUpInfo.accessCode?.valid &&
                        <motion.div className={styles.stepThree__afterTrial} initial={{opacity: 0, y: -20}} animate={{opacity: 1, y: 0}}>After free trial!</motion.div>
                        }
                    </div>
                </div>
                {content.showCodeInput &&
                    <PromoCodeForm />
                }
                <div className={styles.stepThree__badges}>
                    <Badge name="digicert" />
                    <Badge name="bbb" />
                    <Badge name="dss" />
                </div>
                <form onSubmit={(e)=>{e.preventDefault(), handleCreateSubscription()}}>
                    <div>
                        <div className={styles.stepThree__sectionLabel}>Payment information</div>
                        <CardInputsV2 />
                        <div className={styles.stepThree__note}>
                            By completing this transaction you certify that you are 18 years or older, agree to the Privacy Policy of this purchase and have read our Terms and Conditions of this purchase including any recurring terms described above. Promo code users may see a $1 proccessing fee on your account. The processing fee will disappear in 2-4 bussiness days.
                        </div>
                        <div className={styles.stepThree__hint}>
                            <HintMessage type={subscriptionHints?.type} show={subscriptionHints?.type !== ""}>{subscriptionHints?.message}</HintMessage>
                        </div>
                    </div>
                    <div className={styles.stepThree__button}>
                        <Button active={canProceed() && !appLoading}>Continue</Button>
                    </div>
                </form>
            </div>
        </section>
    )
}

const PromoCodeForm = () => {

    const router = useRouter();
    const { signUpInfo, setSignUpInfo, setAppLoading } = useAppContext()
    const [accessCodeHints, setAccessCodeHints] = useState({message:"", type:""})

    const handleSubmitCode = useCallback(async (code=false) => {
        setAppLoading(true)
        try{
            const response = await validateAccessCode(code || signUpInfo.accessCode.code)
            if(response.data.status === "success"){
                setSignUpInfo({...signUpInfo, accessCode: {...signUpInfo.accessCode, num_months: response.data.num_months, valid: true}})
                setAccessCodeHints({message: "", type:""})
            }else{
                setSignUpInfo({...signUpInfo, accessCode: {...signUpInfo.accessCode, valid: false}})
                setAccessCodeHints({message: "Code is not valid", type:"error"})
            }
        }catch(e){
            Sentry.captureException(e);
            setAccessCodeHints({message: "An unexpected error occured.", type:"error"})
        }
        setAppLoading(false)
    }, [signUpInfo, setAppLoading, setSignUpInfo])

    useEffect(()=>{
        if(signUpInfo.initialized && !signUpInfo.accessCode.valid){
            const {code} = router.query
            if(code) handleSubmitCode(code)
        }
    }, [signUpInfo.initialized, signUpInfo.accessCode.valid, router.query, handleSubmitCode])

    return (
        <div className={styles.PromoCode}>
            <div className={styles.sectionLabel}>Promo code</div>
            <form onSubmit={(e)=>{e.preventDefault(), handleSubmitCode()}}>
                <Input type="text" placeholder="Enter Access Code!" onChange={(code)=>{setSignUpInfo({...signUpInfo, accessCode:{...signUpInfo.accessCode, code}})}}  value={signUpInfo.accessCode.code !== false ? signUpInfo.accessCode.code : ""} disabled={signUpInfo.accessCode.valid} />
                <Button>{signUpInfo.accessCode.valid ? tick() : 'Apply'}</Button>
            </form>
            {signUpInfo.accessCode.valid &&
                <motion.div {...getMotionProps("slideUp")} className={styles.codeApplied}>Code applied!</motion.div>
            }
            <HintMessage type={accessCodeHints?.type} show={accessCodeHints?.message !== ""}>{accessCodeHints?.message}</HintMessage>
        </div>
    )
}

export default SignUp
